/**
 * Stock Actions Configuration Registry
 * 
 * Single source of truth for all stock actions available in StockActionsModal.
 * This config-driven approach ensures:
 * - Easy to add/remove actions (just modify this file)
 * - Consistent behavior across all actions
 * - Type-safe action handling
 * 
 * IMPORTANT: Stock Entry Type values must match ERPNext's Stock Entry Type options exactly.
 * Verify in bench console: frappe.get_meta("Stock Entry").get_field("stock_entry_type").options
 */

import {
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowLeftRight,
  PackagePlus,
  RefreshCw,
  FileText,
  Calculator,
  type LucideIcon,
} from "lucide-react";

export type StockActionKind =
  | "stock_entry_quick_action"
  | "stock_reconciliation"
  | "opening_stock";

export type WarehouseMode = "to" | "from" | "from_to";
export type ValueDisplayMode = "incoming" | "outgoing" | "transfer";

export type StockActionId =
  | "material_receipt"
  | "material_issue"
  | "material_transfer"
  | "manufacturing_entry"
  | "repack_entry"
  | "opening_stock"
  | "stock_reconciliation";

export interface StockActionConfig {
  id: StockActionId;
  label: string;
  description: string;
  labelKey?: string; // i18n key for common:ui_* (pass key without "ui_")
  descriptionKey?: string; // i18n key for common:ui_* (pass key without "ui_")
  kind: StockActionKind;
  icon: LucideIcon;

  // Only for kind === "stock_entry_quick_action"
  stockEntryType?: string; // Must match ERPNext Stock Entry Type options exactly
  warehouseMode?: WarehouseMode;
  valueDisplay?: ValueDisplayMode;
}

/**
 * Stock Actions Registry
 * 
 * All 7 stock actions with their configuration.
 * Adding a new action = add one entry here.
 */
export const STOCK_ACTIONS: StockActionConfig[] = [
  {
    id: "material_receipt",
    label: "Material Receipt",
    description: "Increase stock quantity (purchase, production, etc.)",
    labelKey: "stock_action_material_receipt",
    descriptionKey: "stock_action_material_receipt_desc",
    kind: "stock_entry_quick_action",
    stockEntryType: "Material Receipt",
    warehouseMode: "to",
    valueDisplay: "incoming",
    icon: ArrowDownCircle,
  },
  {
    id: "material_issue",
    label: "Material Issue",
    description: "Decrease stock quantity (consumption, waste, etc.)",
    labelKey: "stock_action_material_issue",
    descriptionKey: "stock_action_material_issue_desc",
    kind: "stock_entry_quick_action",
    stockEntryType: "Material Issue",
    warehouseMode: "from",
    valueDisplay: "outgoing",
    icon: ArrowUpCircle,
  },
  {
    id: "material_transfer",
    label: "Material Transfer",
    description: "Transfer stock between warehouses",
    labelKey: "stock_action_material_transfer",
    descriptionKey: "stock_action_material_transfer_desc",
    kind: "stock_entry_quick_action",
    stockEntryType: "Material Transfer",
    warehouseMode: "from_to",
    valueDisplay: "transfer",
    icon: ArrowLeftRight,
  },
  {
    id: "manufacturing_entry",
    label: "Manufacturing Entry",
    description: "Record finished goods from production",
    labelKey: "stock_action_manufacturing_entry",
    descriptionKey: "stock_action_manufacturing_entry_desc",
    kind: "stock_entry_quick_action",
    stockEntryType: "Manufacture", // VERIFY: Must match ERPNext option exactly
    warehouseMode: "from_to",
    valueDisplay: "transfer",
    icon: PackagePlus,
  },
  {
    id: "repack_entry",
    label: "Repack Entry",
    description: "Repackage items (e.g., bulk to retail units)",
    labelKey: "stock_action_repack_entry",
    descriptionKey: "stock_action_repack_entry_desc",
    kind: "stock_entry_quick_action",
    stockEntryType: "Repack", // VERIFY: Must match ERPNext option exactly
    warehouseMode: "from_to",
    valueDisplay: "transfer",
    icon: RefreshCw,
  },
  {
    id: "opening_stock",
    label: "Opening Stock",
    description: "Set initial stock quantity for a warehouse",
    labelKey: "stock_action_opening_stock",
    descriptionKey: "stock_action_opening_stock_desc",
    kind: "opening_stock",
    icon: FileText,
  },
  {
    id: "stock_reconciliation",
    label: "Stock Reconciliation",
    description: "Reconcile physical stock with system records",
    labelKey: "stock_action_stock_reconciliation",
    descriptionKey: "stock_action_stock_reconciliation_desc",
    kind: "stock_reconciliation",
    icon: Calculator,
  },
];

/**
 * Get action config by ID
 */
export function getStockAction(id: StockActionId): StockActionConfig | undefined {
  return STOCK_ACTIONS.find(a => a.id === id);
}

/**
 * Get all actions of a specific kind
 */
export function getStockActionsByKind(kind: StockActionKind): StockActionConfig[] {
  return STOCK_ACTIONS.filter(a => a.kind === kind);
}

